import {
  Autocomplete,
  Checkbox,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getBusinessCrossInvoicesSummary } from '../../../../../lib/api';
import EnhacedBox from '../../../../../lib/designSystem/Box';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import SimpleLineChart from '../../../../../lib/designSystem/LineChart';
import MonthSelector from '../../../../../lib/designSystem/MonthSelector';
import EnhancedTable from '../../../../../lib/designSystem/Table';
import { formatMoney } from '../../../../../lib/functions/utils';

interface CrossInvoiceItem {
  identifier: string;
  name: string;
  emittedAmount: number;
  emittedCount: number;
  recievedAmount: number;
  recievedCount: number;
  crossInvoices: number;
  crossAmount: number;
}

interface MonthMetadata {
  key: string;
  displayName: string;
  year: number;
  month: number;
}

interface DataPayers {
  total: CrossInvoiceItem[];
  [key: string]: CrossInvoiceItem[];
}

interface CrossInvoicesData {
  dataPayers: DataPayers;
  monthsMetadata: MonthMetadata[];
}

interface ChartDataItem {
  name: string;
  'Monto cruzado total': number;
  'Monto cruzado V': number;
  'Monto cruzado C': number;
}

const DashboardSummary: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [crossInvoicesData, setCrossInvoicesData] = useState<CrossInvoicesData>(
    {
      dataPayers: { total: [] },
      monthsMetadata: [],
    },
  );
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [totals, setTotals] = useState({
    totalCrossAmount: 0,
    totalEmittedAmount: 0,
    totalReceivedAmount: 0,
    averageCrossAmount: 0,
    averageEmittedAmount: 0,
    averageReceivedAmount: 0,
  });
  const [selectedPeriod, setSelectedPeriod] = useState<string[]>([
    'Últimos 24M',
  ]);
  const [selectedBusinessFilter, setSelectedBusinessFilter] =
    useState<any>(null);
  const [filteredBusinessTable, setFilteredBusinessTable] = useState<any>([]);
  const [selectedMonthsTable, setSelectedMonthsTable] = useState<string[]>([]);
  const { id } = useParams<{ id: string }>();

  const processChartData = (
    data: CrossInvoicesData,
    selectedBusinessFilterValue?: any,
  ): ChartDataItem[] => {
    const monthsMetadata = data.monthsMetadata || [];
    console.log(
      'selectedBusinessFilterValue',
      selectedBusinessFilterValue,
      selectedBusinessFilter,
    );
    const selectedBusiness = selectedBusinessFilterValue;

    return monthsMetadata.map((monthMeta: MonthMetadata) => {
      let monthData = data.dataPayers?.[monthMeta.key] || [];
      if (selectedBusiness?.identifier) {
        monthData = monthData.filter(
          (item: CrossInvoiceItem) =>
            item.identifier === selectedBusiness.identifier,
        );
      }

      const totalCrossAmount = monthData.reduce(
        (sum: number, item: CrossInvoiceItem) => sum + (item.crossAmount || 0),
        0,
      );
      const totalEmittedAmount = monthData.reduce(
        (sum: number, item: CrossInvoiceItem) =>
          sum + (item.emittedAmount || 0),
        0,
      );
      const totalReceivedAmount = monthData.reduce(
        (sum: number, item: CrossInvoiceItem) =>
          sum + (item.recievedAmount || 0),
        0,
      );

      return {
        name: monthMeta.displayName,
        'Monto cruzado total': totalCrossAmount,
        'Monto cruzado V': totalEmittedAmount,
        'Monto cruzado C': totalReceivedAmount,
      };
    });
  };

  const calculateTotals = (chartData: ChartDataItem[]) => {
    const totalCrossAmount = chartData.reduce(
      (sum, item) => sum + item['Monto cruzado total'],
      0,
    );
    const totalEmittedAmount = chartData.reduce(
      (sum, item) => sum + item['Monto cruzado V'],
      0,
    );
    const totalReceivedAmount = chartData.reduce(
      (sum, item) => sum + item['Monto cruzado C'],
      0,
    );

    const monthsCount = chartData.length || 1;

    setTotals({
      totalCrossAmount,
      totalEmittedAmount,
      totalReceivedAmount,
      averageCrossAmount: totalCrossAmount / monthsCount,
      averageEmittedAmount: totalEmittedAmount / monthsCount,
      averageReceivedAmount: totalReceivedAmount / monthsCount,
    });
  };

  const getFilteredChartData = (data: ChartDataItem[], period: string[]) => {
    if (!period.length) return data;

    const periodMap: { [key: string]: number } = {
      'Últimos 3M': 3,
      'Últimos 6M': 6,
      'Últimos 12M': 12,
      'Últimos 18M': 18,
      'Últimos 24M': 24,
    };

    const selectedMonths = periodMap[period[0]] || 24;
    const filteredData = data.slice(-selectedMonths);
    // if (selectedBusinessFilter?.identifier) {
    //   filteredData = filteredData.filter(
    //     (item) => item.name === selectedBusinessFilter.name,
    //   );
    // }
    return filteredData;
  };

  useEffect(() => {
    const fetchRelations = async () => {
      if (!id) return;
      const crossInvoices = await getBusinessCrossInvoicesSummary(dispatch, id);
      setCrossInvoicesData(
        crossInvoices || {
          dataPayers: { total: [] },
          monthsMetadata: [],
        },
      );

      if (crossInvoices) {
        const processedChartData = processChartData(crossInvoices);
        setChartData(processedChartData);
      }

      setLoading(false);
    };
    fetchRelations();
  }, [id, dispatch]);

  useEffect(() => {
    if (chartData.length > 0) {
      const filteredData = getFilteredChartData(chartData, selectedPeriod);
      calculateTotals(filteredData);
    }
  }, [chartData, selectedPeriod]);

  useEffect(() => {
    if (crossInvoicesData.dataPayers.total) {
      setFilteredBusinessTable(getFilteredTableData());
    }
  }, [
    crossInvoicesData.dataPayers.total,
    selectedMonthsTable,
    selectedBusinessFilter,
  ]);

  useEffect(() => {
    if (
      crossInvoicesData.dataPayers &&
      Object.keys(crossInvoicesData.dataPayers).length > 1
    ) {
      const availableMonths = Object.keys(crossInvoicesData.dataPayers).filter(
        (key) => key !== 'total',
      );
      setSelectedMonthsTable(availableMonths);
    }
  }, [crossInvoicesData.dataPayers]);

  const getFilteredTableData = () => {
    let dataToFilter = [];

    if (selectedMonthsTable.length === 0) {
      dataToFilter = crossInvoicesData.dataPayers.total;
    } else {
      const combinedData = selectedMonthsTable.reduce(
        (acc: any[], monthKey: string) => {
          const monthData = crossInvoicesData.dataPayers[monthKey];
          if (Array.isArray(monthData)) {
            return acc.concat(monthData);
          }
          return acc;
        },
        [],
      );

      const groupedData = combinedData.reduce((acc: any, item: any) => {
        if (!acc[item.identifier]) {
          acc[item.identifier] = {
            identifier: item.identifier,
            name: item.name,
            emittedAmount: 0,
            emittedCount: 0,
            recievedAmount: 0,
            recievedCount: 0,
            crossInvoices: 0,
            crossAmount: 0,
          };
        }

        acc[item.identifier].emittedAmount += item.emittedAmount || 0;
        acc[item.identifier].emittedCount += item.emittedCount || 0;
        acc[item.identifier].recievedAmount += item.recievedAmount || 0;
        acc[item.identifier].recievedCount += item.recievedCount || 0;
        acc[item.identifier].crossInvoices += item.crossInvoices || 0;
        acc[item.identifier].crossAmount += item.crossAmount || 0;

        return acc;
      }, {});

      dataToFilter = Object.values(groupedData);
    }

    if (selectedBusinessFilter) {
      const filteredData = dataToFilter.filter(
        (item: any) => item.identifier === selectedBusinessFilter.identifier,
      );
      return Array.isArray(filteredData) ? filteredData : [];
    }
    return Array.isArray(dataToFilter) ? dataToFilter : [];
  };

  const handleChangeBusiness = (event: any, newValue: any) => {
    setSelectedBusinessFilter(newValue);
    setFilteredBusinessTable(getFilteredTableData());
    setChartData(processChartData(crossInvoicesData, newValue));
  };

  const filterBusinesses = () => {
    const availableBusinesses = getFilteredTableData();

    return (
      <div style={{ width: 'auto', minWidth: '200px' }}>
        <Autocomplete
          size="small"
          options={availableBusinesses || []}
          value={selectedBusinessFilter}
          getOptionLabel={(option: any) =>
            `${option.name} - (${option.identifier})`
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Empresa"
              size="small"
              style={{ minWidth: '200px' }}
            />
          )}
          onChange={handleChangeBusiness}
        />
      </div>
    );
  };

  const TableMonthFilter = () => {
    const availableMonths = Object.keys(crossInvoicesData.dataPayers).filter(
      (key) => key !== 'total',
    );

    const sortedMonths = availableMonths.sort((a, b) => {
      const dateA = new Date(a + '-01');
      const dateB = new Date(b + '-01');
      return dateB.getTime() - dateA.getTime();
    });

    return (
      <MonthSelector
        selectedMonths={selectedMonthsTable}
        onMonthsChange={setSelectedMonthsTable}
        availableMonths={sortedMonths}
        placeholder="Seleccionar meses"
        size="small"
        minWidth="200px"
      />
    );
  };

  if (loading) return <CenteredCircularProgress />;

  const SummaryBox: React.FC<{
    content?: React.ReactNode;
    children?: React.ReactNode;
  }> = ({ content, children }) => {
    return (
      <div
        style={{
          border: '1px solid #e0e0e0',
          padding: '30px 0px',
          background: '#ffffff',
          borderRadius: '10px',
          width: '100%',
        }}
      >
        {content}
        {children}
      </div>
    );
  };

  const PeriodFilter = () => {
    const handleSelectChange = (event: any) => {
      const newSelectedPeriod = event.target.value;
      if (newSelectedPeriod.length === 0)
        return setSelectedPeriod(['Últimos 24M']);

      if (newSelectedPeriod.includes('Últimos 24M')) {
        if (!selectedPeriod.includes('Últimos 24M')) {
          return setSelectedPeriod(['Últimos 24M']);
        }
      }

      if (newSelectedPeriod.includes('Últimos 18M')) {
        if (!selectedPeriod.includes('Últimos 18M')) {
          return setSelectedPeriod(['Últimos 18M']);
        }
      }

      if (newSelectedPeriod.includes('Últimos 12M')) {
        if (!selectedPeriod.includes('Últimos 12M')) {
          return setSelectedPeriod(['Últimos 12M']);
        }
      }

      if (newSelectedPeriod.includes('Últimos 6M')) {
        if (!selectedPeriod.includes('Últimos 6M')) {
          return setSelectedPeriod(['Últimos 6M']);
        }
      }

      if (newSelectedPeriod.includes('Últimos 3M')) {
        if (!selectedPeriod.includes('Últimos 3M')) {
          return setSelectedPeriod(['Últimos 3M']);
        }
      }

      const allSelectedAlready =
        selectedPeriod.includes('Últimos 24M') ||
        selectedPeriod.includes('Últimos 18M') ||
        selectedPeriod.includes('Últimos 12M') ||
        selectedPeriod.includes('Últimos 6M') ||
        selectedPeriod.includes('Últimos 3M');

      if (allSelectedAlready && newSelectedPeriod.length > 1) {
        const newSelectedPeriodFiltered = newSelectedPeriod.filter(
          (period: any) =>
            period !== 'Últimos 24M' &&
            period !== 'Últimos 18M' &&
            period !== 'Últimos 12M' &&
            period !== 'Últimos 6M' &&
            period !== 'Últimos 3M',
        );
        return setSelectedPeriod(newSelectedPeriodFiltered);
      }
      setSelectedPeriod(newSelectedPeriod);
    };

    const periodOptions = [
      'Últimos 3M',
      'Últimos 6M',
      'Últimos 12M',
      'Últimos 18M',
      'Últimos 24M',
    ];

    return (
      <div style={{ width: 'auto', minWidth: '200px' }}>
        <FormControl size="small">
          <Select
            labelId="period-select-label"
            id="period-select"
            multiple
            value={selectedPeriod}
            onChange={handleSelectChange}
            renderValue={(selected) => selected.join(', ')}
            style={{ minWidth: '200px' }}
          >
            {periodOptions.map((period) => (
              <MenuItem key={period} value={period}>
                <Checkbox checked={selectedPeriod.indexOf(period) > -1} />
                <ListItemText primary={period} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  };

  return (
    <div>
      <Grid container spacing={2} rowSpacing={1}>
        <Grid item xs={12}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h4">Facturas Cruzadas</Typography>
            <PeriodFilter />
          </div>
        </Grid>
        <Grid item xs={12}>
          <SummaryBox
            content={
              <>
                <div
                  style={{
                    padding: '0px 8px 10px 8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Typography variant="body2">
                      Evolución de los{' '}
                      {selectedPeriod[0]?.toLowerCase() || 'últimos 24 meses'}
                    </Typography>
                  </div>
                </div>
                <SimpleLineChart
                  data={getFilteredChartData(chartData, selectedPeriod)}
                  aspect={4.0 / 1.08}
                  noMoney={false}
                />
              </>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <EnhacedBox small margin="0px" padding="12px">
                <Typography
                  variant="h5"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: '4px',
                  }}
                >
                  {formatMoney(totals.totalCrossAmount)}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Total cruzado
                </Typography>
              </EnhacedBox>
            </Grid>
            <Grid item xs={4}>
              <EnhacedBox small margin="0px" padding="12px">
                <Typography
                  variant="h5"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: '4px',
                  }}
                >
                  {formatMoney(totals.totalEmittedAmount)}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Facturas emitidas
                </Typography>
              </EnhacedBox>
            </Grid>
            <Grid item xs={4}>
              <EnhacedBox small margin="0px" padding="12px">
                <Typography
                  variant="h5"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: '4px',
                  }}
                >
                  {formatMoney(totals.totalReceivedAmount)}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Facturas recibidas
                </Typography>
              </EnhacedBox>
            </Grid>

            <Grid item xs={4}>
              <EnhacedBox small margin="0px" padding="12px">
                <Typography
                  variant="h5"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: '4px',
                  }}
                >
                  {formatMoney(totals.averageCrossAmount)}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Total cruzado (prom.)
                </Typography>
              </EnhacedBox>
            </Grid>
            <Grid item xs={4}>
              <EnhacedBox small margin="0px" padding="12px">
                <Typography
                  variant="h5"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: '4px',
                  }}
                >
                  {formatMoney(totals.averageEmittedAmount)}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Facturas emitidas (prom.)
                </Typography>
              </EnhacedBox>
            </Grid>
            <Grid item xs={4}>
              <EnhacedBox small margin="0px" padding="12px">
                <Typography
                  variant="h5"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: '4px',
                  }}
                >
                  {formatMoney(totals.averageReceivedAmount)}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Facturas recibidas (prom.)
                </Typography>
              </EnhacedBox>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ marginTop: '24px' }}>
          <Typography variant="body1" style={{ marginBottom: '16px' }}>
            Acá encontrarás las facturas que esta empresa se ha emitido y
            recibido con sus relacionados.
          </Typography>

          <div
            style={{
              border: '1px solidrgb(12, 11, 11)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              background: '#ffffff',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flex: 1 }}>{filterBusinesses()}</div>
              <div style={{ flex: 1 }}>
                <TableMonthFilter />
              </div>
            </div>
          </div>

          <div
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '16px',
              background: '#ffffff',
            }}
          >
            <EnhancedTable
              finalSumRow
              exportToFile="xlsx"
              tableSize="small"
              rowsPerPageDefault={10}
              dataHeaders={[
                { key: 'name', label: 'Razon Social' },
                { key: 'identifier', label: 'Rut' },
                { key: 'emittedCount', label: 'Facturas emitidas' },
                {
                  key: 'emittedAmount',
                  label: 'Monto emitido',
                  type: 'money',
                },
                { key: 'recievedCount', label: 'Facturas recibidas' },
                {
                  key: 'recievedAmount',
                  label: 'Monto recibido',
                  type: 'money',
                },
              ]}
              data={filteredBusinessTable}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default DashboardSummary;
